import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Send, CheckCircle2, AlertCircle, ArrowRight, ChevronDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { cn } from "../lib/utils";

const mainTopics = [
  "Vejen til Recovery",
  "Ta Ja-Hatten På!",
  "Spørg Ikke men spørg",
  "Motivation & Nærvær"
];

const offeringsTopics = [
  "Foredrag til sundhedsprofessionelle og tværfaglige teams",
  "Senfølger efter seksuelle overgreb i barndommen",
  "Psykiske- og somatiske lidelser i sundhedsvæsenet",
  "Patientperspektiv og fortællinger fra egne erfaringer",
  "Kommunikation, recovery og tryghed i mødet",
  "Kommunikation med psykisksårbare patienter",
  "Den svære samtale – Træning i ro og tydelighed",
  "Humor og selvindsigt i patientforløb",
  "Tværfaglig kommunikation og fælles sprog",
  "Når ord gør en forskel",
  "Redskaber, når patienten bryder sammen",
  "Fra idé til virkelighed"
];

const allTopics = [...mainTopics, ...offeringsTopics, "Andet / Skræddersyet"];

export default function Kontakt() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    topics: [],
    website_url: "", // Honeypot
  });

  useEffect(() => {
    const preselected = searchParams.get("emne");
    if (preselected && allTopics.includes(preselected)) {
      setFormData(prev => ({ ...prev, topics: [preselected] }));
    }
  }, [searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectTopic = (topic) => {
    setFormData(prev => ({
      ...prev,
      topics: topic ? [topic] : []
    }));
    setIsOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
          topics: [],
          website_url: "",
        });
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setStatus("error");
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt & Booking – Ta' JA-Hatten På! v/ Candra Gaarde</title>
        <meta name="description" content="Book et foredrag eller tag en uforpligtende snak. Kontakt Candra Gaarde her via formularen, e-mail eller telefon for spørgsmål og forespørgsler." />
      </Helmet>

      <section className="pt-32 pb-20 bg-[#fafafa]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-12 gap-24">
            {/* Info Column */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <span className="text-red-600 font-semibold tracking-[0.3em] uppercase text-xs mb-8 block">Kontakt</span>
                <h1 className="text-4xl md:text-5xl font-normal text-slate-900 leading-tight mb-12 tracking-tight">
                  Lad os <br />
                  <span className="italic font-serif text-red-600">begynde</span> dialogen
                </h1>
                <p className="text-lg md:text-xl text-slate-600 mb-12 leading-relaxed font-light">
                  Har du spørgsmål til et foredrag, eller ønsker du et uforpligtende tilbud? Udfyld formularen, så vender jeg tilbage hurtigst muligt.
                </p>

                <p className="text-lg md:text-xl font-serif italic text-red-600 mb-12">
                  Glæder mig til at høre fra dig!
                </p>

                <div className="space-y-12">
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Telefon</p>
                    <a href="tel:+4522290935" className="text-2xl font-light text-slate-900 hover:text-red-600 transition-colors tracking-tight">22 29 09 35</a>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Email</p>
                    <a href="mailto:Kontakt@ja-hatten.dk" className="text-2xl font-light text-slate-900 hover:text-red-600 transition-colors tracking-tight">Kontakt@ja-hatten.dk</a>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Lokation</p>
                    <p className="text-2xl font-light text-slate-900 tracking-tight">Jeg dækker det meste af landet</p>
                  </div>
                </div>

                <div className="mt-16 pt-8 border-t border-slate-200">
                  <p className="text-sm text-slate-400 font-medium">Ta Ja-Hatten På! — CVR: 46313291</p>
                </div>
              </motion.div>
            </div>

            {/* Form Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-7 bg-white p-8 md:p-16 border border-slate-100 shadow-[0_40px_100px_rgba(0,0,0,0.03)]"
            >
              {status === "success" ? (
                <div className="text-center py-24">
                  <div className="w-16 h-16 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-8">
                    <CheckCircle2 size={32} strokeWidth={1.5} />
                  </div>
                  <h2 className="text-3xl font-serif italic mb-6 text-slate-900">Tak for din besked</h2>
                  <p className="text-slate-500 text-lg mb-12 font-light">Jeg har modtaget din henvendelse og vender tilbage til dig snarest muligt.</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="text-sm font-bold tracking-[0.3em] uppercase text-slate-900 border-b border-slate-900 pb-1 hover:text-red-600 hover:border-red-600 transition-colors"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-12">
                  {/* Honeypot */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <input
                      type="text"
                      name="website_url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-12">
                    <div className="space-y-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Navn</label>
                      <input
                        required
                        type="text"
                        placeholder="Dit fulde navn"
                        className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Email</label>
                      <input
                        required
                        type="email"
                        placeholder="din@email.dk"
                        className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-12">
                    <div className="space-y-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Telefon</label>
                      <input
                        required
                        type="tel"
                        placeholder="Dit telefonnummer"
                        className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-4">
                      <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Organisation</label>
                      <input
                        type="text"
                        placeholder="Navn på virksomhed"
                        className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Interesseret i</label>
                    <div className="relative" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light text-left flex justify-between items-center cursor-pointer text-slate-800"
                      >
                        <span className={cn(!formData.topics[0] && "text-slate-400")}>
                          {formData.topics[0] || "Vælg et emne eller foredrag..."}
                        </span>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          className="text-slate-400"
                        >
                          <ChevronDown size={20} strokeWidth={1.5} />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.15, ease: "easeOut" }}
                            className="absolute z-50 left-0 right-0 mt-2 bg-white border border-slate-100 shadow-[0_10px_30px_rgba(0,0,0,0.08)] max-h-80 overflow-y-auto rounded-md py-2 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent"
                          >
                            {/* Reset option */}
                            <button
                              type="button"
                              onClick={() => handleSelectTopic("")}
                              className="w-full text-left px-6 py-3 text-sm text-slate-400 hover:bg-slate-50 transition-colors"
                            >
                              Vælg et emne eller foredrag...
                            </button>

                            {/* Main Topics */}
                            <div className="px-6 py-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-bold border-t border-slate-50 mt-1">
                              Hovedtemaer & Foredrag
                            </div>
                            {mainTopics.map((topic) => (
                              <button
                                key={topic}
                                type="button"
                                onClick={() => handleSelectTopic(topic)}
                                className={cn(
                                  "w-full text-left px-8 py-3 text-sm transition-all flex items-center justify-between",
                                  formData.topics[0] === topic
                                    ? "bg-red-50/50 text-red-600 font-medium border-l-2 border-red-600"
                                    : "text-slate-700 hover:bg-slate-50 hover:text-red-600"
                                )}
                              >
                                {topic}
                              </button>
                            ))}

                            {/* Offerings Topics */}
                            <div className="px-6 py-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-bold border-t border-slate-50 mt-2">
                              Specifikke emner jeg dækker
                            </div>
                            {offeringsTopics.map((topic) => (
                              <button
                                key={topic}
                                type="button"
                                onClick={() => handleSelectTopic(topic)}
                                className={cn(
                                  "w-full text-left px-8 py-3 text-sm transition-all flex items-center justify-between",
                                  formData.topics[0] === topic
                                    ? "bg-red-50/50 text-red-600 font-medium border-l-2 border-red-600"
                                    : "text-slate-700 hover:bg-slate-50 hover:text-red-600"
                                )}
                              >
                                {topic}
                              </button>
                            ))}

                            {/* Other */}
                            <div className="px-6 py-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-bold border-t border-slate-50 mt-2">
                              Andet
                            </div>
                            <button
                              type="button"
                              onClick={() => handleSelectTopic("Andet / Skræddersyet")}
                              className={cn(
                                "w-full text-left px-8 py-3 text-sm transition-all flex items-center justify-between",
                                formData.topics[0] === "Andet / Skræddersyet"
                                  ? "bg-red-50/50 text-red-600 font-medium border-l-2 border-red-600"
                                  : "text-slate-700 hover:bg-slate-50 hover:text-red-600"
                                )}
                            >
                              Andet / Skræddersyet
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold">Besked</label>
                    <textarea
                      required
                      rows={4}
                      placeholder="Fortæl mig lidt om jeres behov..."
                      className="w-full bg-transparent border-b border-slate-200 py-4 focus:border-red-600 outline-none transition-colors text-lg font-light resize-none"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  </div>

                  {status === "error" && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4">
                      <AlertCircle size={20} />
                      <p className="text-xs font-bold tracking-widest uppercase">Der skete en fejl. Prøv venligst igen.</p>
                    </div>
                  )}

                  <button
                    disabled={status === "loading"}
                    type="submit"
                    className="group relative inline-flex items-center justify-center px-8 py-4 md:px-12 md:py-6 bg-red-600 text-white font-semibold tracking-widest uppercase transition-all hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Send forespørgsel</span>
                        <ArrowRight className="ml-4 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
